import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { SupportTicketMessageCreateRequest, SupportTicketResponse, TicketStatusUpdateRequestStatusEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import ExceptionManager from '@/managers/exception-manager.tsx';
import MessageContentSupport from '@/screens/messages/components/MessageContentSupport.tsx';
import MessageInput from '@/screens/messages/components/MessageInput.tsx';
import { Message, SenderType, User, UserType } from '@/screens/messages/messages.types.ts';
import DhAvatar from '@/assets/svg/dh-avatar.svg';
import MessagesPlaceholder from '@/assets/svg/messages-placeholder.svg';
import NewSupportTicketModal from '@/screens/messages/components/NewSupportTicketModal.tsx';

interface StudentSupportProps {
	studentId: number;
	studentAvatar?: string;
}

const currentUser: User = {
	id: 0,
	name: '',
	surname: '',
	avatar: DhAvatar,
	type: UserType.KY_APPOINTMENT
};

const TICKET_STATUS = {
    OPEN: 'OPEN',
    IN_PROGRESS: 'IN_PROGRESS',
    WAITING_USER: 'WAITING_USER',
    WAITING_AGENT: 'WAITING_AGENT',
    RESOLVED: 'RESOLVED',
    CLOSED: 'CLOSED'
};

const TICKET_STATUS_CONFIG: Record<any, any> = {
    [TICKET_STATUS.OPEN]: { label: 'Açık', variant: 'blue' },
    [TICKET_STATUS.IN_PROGRESS]: { label: 'İnceleniyor', variant: 'blue' },
    [TICKET_STATUS.WAITING_USER]: { label: 'Yanıtlandı', variant: 'green' },
    [TICKET_STATUS.WAITING_AGENT]: { label: 'Değerlendiriliyor...', variant: 'blue' },
    [TICKET_STATUS.RESOLVED]: { label: 'Çözüldü', variant: 'green' },
    [TICKET_STATUS.CLOSED]: { label: 'Kapatıldı', variant: 'gray' }
};

const STATUS_CLASS: Record<string, string> = {
	OPEN: 'status--open',
	IN_PROGRESS: 'status--in-progress',
	RESOLVED: 'status--resolved',
	CLOSED: 'status--closed'
};

const StudentSupport: React.FC<StudentSupportProps> = ({ studentId, studentAvatar }) => {
	const { SupportTicketControllerApi } = useApi();
	const queryClient = useQueryClient();
	const [activeSubTab] = useState<'ticket'>('ticket');
	const [selectedTicket, setSelectedTicket] = useState<SupportTicketResponse | null>(null);
	const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);

	const { data: ticketsData, isLoading: isTicketsLoading } = useQueryProvider(
		[QUERY_KEYS.getSupportTickets, studentId],
		() => SupportTicketControllerApi.getSupportTickets(studentId, undefined, undefined, 0, 20, ['createdAt,DESC']),
		{
			enabled: !!studentId,
			select: ({ data }) => data?.data
		}
	);

	const { data: ticketMessagesData, isLoading: isMessagesLoading, refetch: refetchMessages } = useQueryProvider(
		[QUERY_KEYS.getSupportTicketMessages, selectedTicket?.id ?? ''],
		() => SupportTicketControllerApi.getSupportTicketMessages(selectedTicket?.id as number, 0, 50, ['createdAt,ASC']),
		{
			enabled: !!selectedTicket?.id,
			select: ({ data }) => data?.data
		}
	);

	const messages: Message[] = useMemo(() => {
		const items = ticketMessagesData?.messages?.content ?? [];
		return items.map((msg, index) => {
			const senderType = msg.senderType;
			const isExpert = senderType === 'EXPERT';
			const isStudent = senderType === 'STUDENT';

			return {
				id: index,
				content: msg.content,
				date: dayjs(msg.createdAt).format('YYYY-MM-DD'),
				time: dayjs(msg.createdAt).format('HH:mm'),
				sender: isExpert ? SenderType.USER : SenderType.STUDENT,
                senderType: msg.senderType,
				senderUser: isExpert
					? undefined
					: {
							id: 0,
							name: isStudent ? '' : 'Teknik',
							surname: isStudent ? '' : 'Destek',
							avatar: isStudent ? (studentAvatar ?? '') : DhAvatar
						},
				isRead: true
			};
		});
	}, [ticketMessagesData, studentAvatar]);

	const updateTicketStatusMutation = useMutationProvider(
		({ ticketId, status }: { ticketId: number; status: TicketStatusUpdateRequestStatusEnum }) => SupportTicketControllerApi.updateSupportTicketStatus(ticketId, { status }),
		{
			onSuccess: (_data, { status }) => {
				setSelectedTicket(prev => (prev ? { ...prev, status } : prev));
				queryClient.invalidateQueries([QUERY_KEYS.getSupportTickets, studentId]);
			},
			onError: ExceptionManager.errorHandler
		}
	);

	const sendMessageMutation = useMutationProvider(
		(body: SupportTicketMessageCreateRequest) => SupportTicketControllerApi.createTicketMessage(selectedTicket?.id as number, body),
		{
			onSuccess: () => {
				refetchMessages();
				if (selectedTicket?.id) {
					updateTicketStatusMutation.mutate({ ticketId: selectedTicket.id, status: TicketStatusUpdateRequestStatusEnum.WaitingAgent });
				}
			}
		}
	);

	const handleTicketClick = useCallback((ticket: SupportTicketResponse) => {
		setSelectedTicket(ticket);
	}, []);

	const handleBack = useCallback(() => {
		setSelectedTicket(null);
	}, []);

	const handleSendMessage = useCallback(
		(content: string) => {
			if (!content.trim() || !selectedTicket?.id) return;
			sendMessageMutation.mutate({ content });
		},
		[selectedTicket?.id, sendMessageMutation]
	);

	const tickets = ticketsData?.content ?? [];

	return (
		<div className="student-support">
			<div className="new-support-ticket-banner">
				<div className="new-support-ticket-banner__info">
					<div className="info-title">Destek Talepleri</div>
					<div className="info-description">
						Öğrencinin açtığı destek taleplerini buradan takip edebilirsiniz.
					</div>
				</div>
				<div className="new-support-ticket-banner__action">
					<button
						className="create-button"
						onClick={() => setIsNewTicketModalOpen(true)}>
						<span className="create-button-icon">
							<i className="icons-add-circle" />
						</span>
						<span className="create-button-label">Destek Talebi Oluştur</span>
					</button>
				</div>
			</div>
			<div className="student-support__sub-tabs">
				<div className={clsx('student-support__sub-tabs-item', { 'student-support__sub-tabs-item--active': activeSubTab === 'ticket' })}>Ticket</div>
			</div>

			{selectedTicket ? (
				<div className="student-support__ticket-detail">
					<div className="student-support__ticket-detail-header">
						<button
							className="student-support__back-button"
							onClick={handleBack}>
							<i className="icons-arrow-left" />
						</button>
						<div className="student-support__ticket-detail-info">
							<div className="student-support__ticket-detail-title">{selectedTicket.subject}</div>
							<div className="student-support__ticket-detail-meta">
								<span className="student-support__status-label">Durum:</span>
								<span className={clsx('student-support__status', STATUS_CLASS[selectedTicket.status ?? ''])}>{TICKET_STATUS_CONFIG[selectedTicket.status ?? '']?.label ?? selectedTicket.status}    </span>
								<span className="student-support__ticket-detail-date">{dayjs(selectedTicket.createdAt).format('DD MMMM YYYY')}</span>
							</div>
						</div>
					</div>

					{isMessagesLoading ? (
						<div className="student-support__loading">
							<div className="spinner" />
						</div>
					) : (
						<MessageContentSupport
							messages={messages}
							currentUser={currentUser}
						/>
					)}

					<MessageInput
						onSendMessage={handleSendMessage}
						isSupport
					/>
				</div>
			) : (
				<div className="student-support__ticket-list">
					{isTicketsLoading ? (
						<div className="student-support__loading">
							<div className="spinner" />
						</div>
					) : tickets.length > 0 ? (
						tickets.map(ticket => (
							<div
								key={ticket.id}
								className="student-support__ticket-item"
								onClick={() => handleTicketClick(ticket)}>
								<div className="student-support__ticket-item-icon">
									<i className="icons-dialog" />
								</div>
								<div className="student-support__ticket-item-content">
									<div className="student-support__ticket-item-subject">{ticket.subject}</div>
									<div className="student-support__ticket-item-message">{ticket.lastMessage}</div>
								</div>
								<div className="student-support__ticket-item-right">
									<div className="student-support__ticket-item-status">
										<span className="student-support__status-label">Durum:</span>
										<span className={clsx('student-support__status', STATUS_CLASS[ticket.status ?? ''])}>{TICKET_STATUS_CONFIG[ticket.status ?? '']?.label ?? ticket.status}</span>
									</div>
									<div className="student-support__ticket-item-date">{dayjs(ticket.lastMessageDate ?? ticket.createdAt).format('DD MMM YYYY')}</div>
								</div>
								<i className="icons-arrow-right student-support__ticket-item-arrow" />
							</div>
						))
					) : (
						<div className="student-support__empty">
							<img
								src={MessagesPlaceholder}
								alt="empty"
							/>
							<p>Henüz bir destek talebi bulunmamaktadır.</p>
						</div>
					)}
				</div>
			)}

			<NewSupportTicketModal
				isOpen={isNewTicketModalOpen}
				onClose={() => setIsNewTicketModalOpen(false)}
				studentId={studentId}
			/>
		</div>
	);
};

export default StudentSupport;
